import { apiPost, apiGet } from './api';

/**
 * Open Razorpay Checkout for the given plan.
 *
 * Routes automatically based on plan_key suffix:
 *   • _monthly / _yearly  → calls /payments/razorpay/create-subscription and
 *     opens Checkout in SUBSCRIPTION mode (mandate auth). Tier flip happens
 *     server-side on the subscription.activated webhook (decision 1a) — the
 *     handler returns ok=true immediately on Checkout success since the user
 *     has set up their mandate.
 *   • _lifetime / basic_lifetime → calls /create-order (existing one-time
 *     flow) and verifies via /verify.
 *
 * @returns {Promise<{ok: boolean, new_tier?: string, message?: string}>}
 */
export async function openRazorpayCheckout(planKey, { onSuccess, onFailure } = {}) {
  const isRecurring = planKey.endsWith('_monthly') || planKey.endsWith('_yearly');
  return isRecurring
    ? _openSubscriptionCheckout(planKey, { onSuccess, onFailure })
    : _openOrderCheckout(planKey, { onSuccess, onFailure });
}

// One-time order flow (lifetime plans, basic_lifetime)
async function _openOrderCheckout(planKey, { onSuccess, onFailure } = {}) {
  let order;
  try {
    order = await apiPost('/payments/razorpay/create-order', { plan_key: planKey });
  } catch (e) {
    const msg = e?.message || 'Could not start checkout';
    onFailure?.(msg);
    return { ok: false, message: msg };
  }

  if (order.is_mock || !order.key_id) {
    const msg = 'Payments are not yet configured. Admin needs to add Razorpay API keys to enable real checkout.';
    onFailure?.(msg);
    return { ok: false, message: msg };
  }

  if (typeof window === 'undefined' || !window.Razorpay) {
    const msg = 'Razorpay SDK is still loading. Please try again in a moment.';
    onFailure?.(msg);
    return { ok: false, message: msg };
  }

  return new Promise((resolve) => {
    const rzp = new window.Razorpay({
      key: order.key_id,
      amount: order.amount_paise,
      currency: order.currency || 'INR',
      name: 'Gurukul Veda',
      description: order.plan_label || 'Gurukul Veda Plan',
      order_id: order.order_id,
      prefill: {
        name: order.user?.name || '',
        email: order.user?.email || '',
      },
      theme: { color: '#6366F1' },
      handler: async (response) => {
        try {
          const verify = await apiPost('/payments/razorpay/verify', {
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
            plan_key: planKey,
          });
          onSuccess?.(verify);
          resolve({ ok: true, new_tier: verify.new_tier });
        } catch (e) {
          const msg = e?.message || 'Payment verification failed. If amount was deducted, support will reconcile within 24 hours.';
          onFailure?.(msg);
          resolve({ ok: false, message: msg });
        }
      },
      modal: {
        ondismiss: () => {
          onFailure?.('Checkout closed before payment.');
          resolve({ ok: false, message: 'Cancelled' });
        },
      },
    });
    rzp.open();
  });
}

// Recurring subscription flow (_monthly, _yearly). Razorpay Checkout opens
// in subscription mode — the customer authenticates a UPI Autopay /
// e-mandate / card token instead of paying a one-shot amount. Tier flip
// happens server-side when the `subscription.activated` webhook arrives.
async function _openSubscriptionCheckout(planKey, { onSuccess, onFailure } = {}) {
  let sub;
  try {
    sub = await apiPost('/payments/razorpay/create-subscription', { plan_key: planKey });
  } catch (e) {
    const msg = e?.message || 'Could not start checkout';
    onFailure?.(msg);
    return { ok: false, message: msg };
  }

  if (sub.is_mock || !sub.key_id) {
    const msg = 'Recurring payments are not yet configured. Add live Razorpay keys + enable Subscriptions in the dashboard.';
    onFailure?.(msg);
    return { ok: false, message: msg };
  }

  if (typeof window === 'undefined' || !window.Razorpay) {
    const msg = 'Razorpay SDK is still loading. Please try again in a moment.';
    onFailure?.(msg);
    return { ok: false, message: msg };
  }

  return new Promise((resolve) => {
    const rzp = new window.Razorpay({
      key: sub.key_id,
      // NOTE: no `amount` or `order_id` for subscriptions — Razorpay reads
      // these from the subscription_id we pass below.
      subscription_id: sub.subscription_id,
      name: 'Gurukul Veda',
      description: sub.plan_label || 'Gurukul Veda Subscription',
      prefill: {
        name: sub.user?.name || '',
        email: sub.user?.email || '',
      },
      theme: { color: '#6366F1' },
      handler: async () => {
        // Subscription "success" means the customer set up the mandate.
        // Tier upgrade happens via the subscription.activated webhook —
        // we tell the caller to poll /payments/status briefly.
        onSuccess?.({ recurring: true, subscription_id: sub.subscription_id });
        resolve({
          ok: true,
          recurring: true,
          message: 'Subscription set up — your tier will activate within a minute.',
        });
      },
      modal: {
        ondismiss: () => {
          onFailure?.('Checkout closed before mandate was set up.');
          resolve({ ok: false, message: 'Cancelled' });
        },
      },
    });
    rzp.open();
  });
}

export async function fetchPaymentStatus() {
  try {
    return await apiGet('/payments/status');
  } catch {
    return null;
  }
}
